import type {Step} from '@/app/signup/routing/StepsRouting';
import {getAnalytics} from '@/hooks/useAnalytics';
import {useCallback} from 'react';
import type {SignupAnalyticsService} from '.';
import type {BuyflowType, CurrentStep} from '../TypesOpenApi';
import {
  BACK_ANALYTICS_DATA_BY_PAGE,
  EXCEPTIONAL_ACTIONS,
  NEXT_ANALYTICS_DATA_BY_PAGE,
} from './configuration';
import {ProblemDetail} from '../types';

type TrackButtonArgs = Parameters<SignupAnalyticsService['trackBackButton']>;
/**
 * Determines if buyflow is of type Admin Cx.
 */
export function isAdminCx(buyflowType: BuyflowType | undefined): boolean {
  return buyflowType === 'ADMIN_CX';
}

export function isSmb(buyflowType: BuyflowType | undefined): boolean {
  return buyflowType === 'SMB';
}

export type AnalyticsNavType = 'next' | 'back';

export const getAnalyticNavArgs = (
  currentStep: Step,
  type: AnalyticsNavType,
) => {
  const source =
    type === 'next' ? NEXT_ANALYTICS_DATA_BY_PAGE : BACK_ANALYTICS_DATA_BY_PAGE;
  const stepData = source[currentStep.name as CurrentStep];

  if (typeof stepData === 'function') {
    return stepData(currentStep);
  }

  return stepData;
};

export const useAnalyticsNavButton = (type: AnalyticsNavType) => {
  const sendBackButtonEvent = useCallback(
    (step: Step) => {
      const analytics = getAnalytics();

      const result = getAnalyticNavArgs(step, type);

      if (result) {
        switch (type) {
          case 'next':
            analytics.trackNextButton(...result);
            break;

          case 'back':
            analytics.trackBackButton(
              ...(result as unknown as TrackButtonArgs),
            );
            break;
          default:
            throw Error('Wrong type');
        }
      }
    },
    [type],
  );

  return sendBackButtonEvent;
};

export const getExceptionalText = (
  step: CurrentStep,
  buyflowType?: BuyflowType,
  error?: unknown,
) => {
  const action =
    (step && buyflowType && EXCEPTIONAL_ACTIONS[step]?.[buyflowType]) ||
    EXCEPTIONAL_ACTIONS[step]?.UNIVERSAL;

  const description =
    typeof action === 'function' ? action(step, buyflowType, error) : action;

  return description;
};

export const buyflowRequestExeptionTracking = (
  step: CurrentStep,
  buyflowType?: BuyflowType,
  error?: unknown,
) => {
  const analytics = getAnalytics();
  const description = getExceptionalText(step, buyflowType, error);

  if (description) analytics.sendException(description, {buyflowType});
};

export const isErrorProblemDetail = (error: unknown) =>
  !!(
    error &&
    typeof error === 'object' &&
    ['instance', 'status', 'title'].every(i =>
      Object.keys(error as ProblemDetail).includes(i),
    )
  );

export const openAPIFetchErrorParse = (error: unknown) => {
  if (isErrorProblemDetail(error)) {
    const {title, status, instance} = error as ProblemDetail;

    return `Error: ${status} for ${instance} ${title}`;
  }

  return null;
};
